/**
 * @author      luis.orihuela@zumper.com (Luis Orihuela)
 * @copyright   Copyright (c) 2023, Zumper
 * @description NewMessageForm
 */
import { Box, Heading, Image } from '@chakra-ui/react'
import { Button } from '@zcode-ui/controls'
import { useViewSection } from '@zcode-ui/hook'
import { boltGray0 } from '@zcode-ui/icons'
import { Badges, getFullAddress } from '@zcode-ui/web-zumper-features'
import type { AnalyticsC, Origin } from '@zcode/nr-utils'
import { Form } from '@zcode/nr-utils'
import {
  type APPLY,
  type MESSAGE,
  MESSAGE_FORM_INLINE,
  type MESSAGE_FORM_MODAL,
  TOUR,
  WAITLIST,
} from '@zcode/nr/es/shared/constants/message'
import type {
  FloorplanListing,
  Listable,
  NormalFloorplanListingData,
  Property,
} from '@zcode/state-type'
import { Formik, type FormikHelpers, type FormikProps } from 'formik'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'

import { selectMessageModalStep } from '../../../../modules/modals/selectors/messageModal'
import { LEGAL_COPY_ACTION_TEXT, LegalCopy } from '../../../LegalCopy/LegalCopy'
import { useAlcInit } from '../../AlcMessaging/useAlcInit'
import { ALERT_CHECKBOX_FORM_NAME } from '../../constants'
import { FormInputsContainer } from '../FormInputs/FormInputsContainer'
// Temporarily removing KnockFooter from message form
// import { KnockFooter } from '../KnockFooter/KnockFooter'
import { MessageSent } from '../MessageSent/MessageSent'
import { SpecialtyHousingConfirmation } from '../SpecialtyHousing/SpecialtyHousingConfirmation'
import { SubmitError } from '../SubmitError/SubmitError'
import type { Values } from '../helpers'
import type { getValidationSchema } from '../helpers/getValidationSchema'

import { useHasInstantTourBooking } from '../TourOptions/useHasInstantTourBooking'
import { MessageFormContextProvider, useMessageFormContext } from './MessageFormContext'

type Intent = typeof TOUR | typeof APPLY | typeof MESSAGE | typeof WAITLIST
type FormType = typeof MESSAGE_FORM_MODAL | typeof MESSAGE_FORM_INLINE

export interface NewMessageFormProps<Entity extends Listable | Property, _Origin extends Origin> {
  hasContext?: boolean
  allowMFTourButton?: boolean
  analyticChannel:
    | typeof AnalyticsC.AFFORDABILITY_CALCULATOR
    | typeof AnalyticsC.FULL_DETAIL
    | typeof AnalyticsC.PERMANENT_ADDRESS
    | typeof AnalyticsC.PROGRAMMATIC_RENT
    | typeof AnalyticsC.LIST
    | typeof AnalyticsC.LIST_ITEM
    | typeof AnalyticsC.LIST_ITEM_MESSAGED
    | typeof AnalyticsC.HOMEPAGE
  entity: Entity
  equalCTAIntent: Intent | undefined
  formType: FormType
  hasBoxShadow?: boolean
  hasSelectedTime: boolean
  headerText?: string | null | undefined
  hideAllHeadings?: boolean
  hideCancelButton?: boolean
  hideContactButtons?: boolean
  hideCustomMessage?: boolean
  hideFloorplans?: boolean
  hideForm: boolean
  isGalleryLanding?: boolean
  isModal: boolean | undefined
  isQuestionModal?: boolean
  isSendingMessage: boolean
  initialValues: Values
  messageSentTime: number | null
  onCancel?: React.MouseEventHandler<HTMLButtonElement>
  onEnableResending: () => void
  onlyShowFloorplanGroup?: boolean
  onMessageMouseEnter?: React.MouseEventHandler<HTMLButtonElement>
  onPaidPhoneClick: () => void
  onSetFloorplanUserSelected: React.ChangeEventHandler<HTMLSelectElement>
  onSubmit: (values: Values, actions: FormikHelpers<Values>) => Promise<void>
  onSubmitError: () => void
  onSubmitMFTourRequest: (values: Values) => Promise<void>
  onView?: (params: { entity: Entity; origin: _Origin }) => void
  origin: _Origin
  phoneNumberRequired: boolean
  resending: boolean
  selectedFloorplan?: FloorplanListing<NormalFloorplanListingData>
  sentMessageOnSession: boolean
  showMessageIndicator: boolean
  submittingTourRequest: boolean
  validationSchema: ReturnType<typeof getValidationSchema>
}

const getSubmitButtonText = ({
  equalCTAIntent,
  hasInstantTourAvailability,
  isQuestionModal,
  step,
}: {
  equalCTAIntent: Intent | undefined
  hasInstantTourAvailability: boolean | undefined
  isQuestionModal?: boolean
  step: null | 1 | 2
}) => {
  // Step is only ever equal to 1 for TOUR and on mobile (where we have a two step form)
  if (step === 1) {
    return 'Confirm'
  }
  if (equalCTAIntent === TOUR) {
    return hasInstantTourAvailability ? 'Instant book' : 'Request tour'
  }
  if (equalCTAIntent === WAITLIST) {
    return 'Ask to join waitlist'
  }
  return isQuestionModal ? 'Submit' : 'Check availability'
}

export const MessageForm = <Entity extends Listable | Property, _Origin extends Origin>({
  allowMFTourButton,
  analyticChannel,
  entity,
  equalCTAIntent,
  formType,
  hasBoxShadow,
  hasSelectedTime,
  headerText,
  hideCustomMessage,
  hideFloorplans,
  hideForm,
  initialValues,
  isGalleryLanding,
  isModal,
  isSendingMessage,
  isQuestionModal,
  messageSentTime,
  onlyShowFloorplanGroup,
  onSetFloorplanUserSelected,
  onSubmit,
  onSubmitError,
  onSubmitMFTourRequest,
  onView: _onView,
  origin,
  phoneNumberRequired,
  selectedFloorplan,
  showMessageIndicator,
  submittingTourRequest,
  validationSchema,
}: NewMessageFormProps<Entity, _Origin>) => {
  const { agent, paidPhone, paidPhoneHref } = entity
  const phone = paidPhone || agent?.phone
  const phoneHref = paidPhoneHref || agent?.phoneHref
  const onView = useCallback(() => _onView?.({ entity, origin }), [entity, origin, _onView])
  const ref = useViewSection(onView)
  const step = useSelector(selectMessageModalStep)
  const {
    isSpecialtyHousing,
    formValues,
    isSpecialtyHousingOpen,
    onCloseSpecialtyHousing,
    onOpenSpecialtyHousing,
    setFormValues,
    setIsWaitlistForm,
  } = useMessageFormContext<Values>()
  const { alcCheckbox, isAlcLoading, isAlcCheckboxChecked } = useAlcInit({
    initialCheckboxValue: formValues?.[ALERT_CHECKBOX_FORM_NAME],
  })
  const shouldOpenConfirmation = isSpecialtyHousing && !isSpecialtyHousingOpen && step !== 1
  const isInlineForm = !isModal || origin === 'g-open-scroll' || origin === 'g-open-focus'

  const showHeading = isInlineForm && headerText

  if (formType === MESSAGE_FORM_INLINE && equalCTAIntent !== WAITLIST) {
    const defaultFloorplan = entity.floorplans?.find((fp) => !fp?.waitlistEnabled)
    if (defaultFloorplan) {
      selectedFloorplan = defaultFloorplan
    }
  }

  const { hasInstantTourAvailability } = useHasInstantTourBooking(entity, equalCTAIntent !== TOUR)
  const includesTourTimes = equalCTAIntent === TOUR && hasSelectedTime

  const propertyText = entity.isBuilding
    ? `${entity.name || entity.formattedAddress}`
    : `${entity.formattedAddress || getFullAddress(entity)}` || 'this property'

  const defaultMessageRef = useRef<string>()
  const handleOnSubmit = useCallback(
    async (values: Values, formikBag: FormikHelpers<Values>) => {
      if (step === 1) {
        return
      }
      if (!values.msg && defaultMessageRef.current) {
        values.msg = defaultMessageRef.current
        values.messageModified = false
      }

      if (!entity.isRentpath) {
        values[ALERT_CHECKBOX_FORM_NAME] = isAlcCheckboxChecked
      }
      if (isSpecialtyHousingOpen) {
        onCloseSpecialtyHousing()
      }
      if (includesTourTimes) {
        await onSubmitMFTourRequest(values)
      } else {
        await onSubmit(values, formikBag)
      }
    },
    [
      entity,
      includesTourTimes,
      isAlcCheckboxChecked,
      onSubmit,
      onSubmitMFTourRequest,
      step,
      isSpecialtyHousingOpen,
      onCloseSpecialtyHousing,
    ]
  )

  const handleOpen = (values: Values) => {
    values[ALERT_CHECKBOX_FORM_NAME] = isAlcCheckboxChecked
    setFormValues(values)
    onOpenSpecialtyHousing()
  }
  const isSubmitDisabled =
    isAlcLoading ||
    isSendingMessage ||
    (equalCTAIntent === TOUR && typeof hasInstantTourAvailability === 'undefined')
  const isLoading =
    (includesTourTimes ? submittingTourRequest : showMessageIndicator) || isAlcLoading

  const submitButtonText = useMemo(
    () =>
      getSubmitButtonText({
        equalCTAIntent,
        hasInstantTourAvailability,
        isQuestionModal,
        step,
      }),
    [equalCTAIntent, hasInstantTourAvailability, isQuestionModal, step]
  )

  const isWaitlist = equalCTAIntent === WAITLIST

  useEffect(() => {
    setIsWaitlistForm(isWaitlist)
  }, [isWaitlist, setIsWaitlistForm])

  const submitButton = (
    <Button
      className={`${isGalleryLanding ? 'bg-pink-650' : step === 2 ? 'bg-blue-900' : 'bg-blue-800'} mb-4 mt-2 w-full`}
      aria-describedby={isModal ? 'modal-form-errors' : 'sticky-form-errors'}
      disabled={isSubmitDisabled}
      isLoading={isLoading}
      size="lg"
      type="submit"
      variant={isGalleryLanding ? 'pink' : 'solid'}
    >
      {hasInstantTourAvailability && equalCTAIntent === TOUR ? (
        <Image alt="" height="21px" mr={2} src={boltGray0} width="21px" />
      ) : null}
      {submitButtonText}
    </Button>
  )
  return (
    <Box<'div'>
      maxW={{ base: '100%', md: '680px' }}
      paddingBottom={{ base: showHeading ? 10 : 0, md: isModal && !hasBoxShadow ? 0 : 6 }}
      paddingTop={{ base: showHeading ? 10 : 0, md: isModal && !hasBoxShadow ? 0 : 8 }}
      paddingX={{ base: 0, md: isModal && !hasBoxShadow ? 0 : 6 }}
      {...(hasBoxShadow && {
        borderRadius: { base: 'none', md: 'md' },
        borderWidth: { base: 'none', md: '1px' },
        borderStyle: { base: 'none', md: 'solid' },
        borderColor: { base: 'none', md: 'gray.150' },
        boxShadow: { base: 'none', md: '0px 8px 16px 0px rgba(0, 0, 0, 0.12)' },
      })}
    >
      {showHeading && !hideForm && (
        <Box<'div'>
          display="flex"
          flexDir={{ base: 'column-reverse', lg: 'row' }}
          justifyContent={{ lg: 'space-between' }}
          marginBottom={6}
        >
          <Heading fontSize={{ base: 'xl', lg: 'lg' }}>
            {isWaitlist ? 'Ask to join waitlist' : headerText}
          </Heading>
          {!hideForm && (
            <Badges
              badgeLimit={{ primary: 1 }}
              containerProps={{ marginBottom: { base: 2, lg: 0 } }}
              isPrimaryOnly
            />
          )}
        </Box>
      )}
      <Formik
        enableReinitialize
        initialValues={formValues ?? initialValues}
        onSubmit={shouldOpenConfirmation ? handleOpen : handleOnSubmit}
        validationSchema={validationSchema}
      >
        {(props: FormikProps<Values>) => (
          <>
            <SpecialtyHousingConfirmation
              isButtonDisabled={isSubmitDisabled}
              isLoading={isLoading}
              onSubmit={props.handleSubmit}
              renderInModal={isInlineForm}
            />
            {(!isSpecialtyHousingOpen || isInlineForm) && (
              <Form noValidate ref={ref}>
                <SubmitError onSubmitError={onSubmitError} />
                {hideForm ? (
                  <MessageSent
                    messageSentTime={messageSentTime}
                    paidPhone={phone}
                    paidPhoneHref={phoneHref}
                  />
                ) : (
                  <FormInputsContainer
                    allowMFTourButton={allowMFTourButton}
                    analyticChannel={analyticChannel}
                    defaultMessageRef={defaultMessageRef}
                    entity={entity}
                    equalCTAIntent={equalCTAIntent}
                    hideCustomMessage={hideCustomMessage}
                    hideFloorplans={hideFloorplans}
                    includesTourTimes={includesTourTimes}
                    isModal={isModal}
                    onSetFloorplanUserSelected={onSetFloorplanUserSelected}
                    onlyShowFloorplanGroup={onlyShowFloorplanGroup}
                    origin={origin}
                    phoneNumberRequired={phoneNumberRequired}
                    selectedFloorplan={selectedFloorplan}
                    step={step}
                    submitButton={submitButton}
                  />
                )}
                {!hideForm ? (
                  <>
                    {submitButton}
                    {step !== 1 && (
                      <>
                        <LegalCopy
                          actionText={LEGAL_COPY_ACTION_TEXT.SEND_INQUIRY}
                          fontSize="xs"
                          isRentpath={entity.isRentpath}
                          propertyText={propertyText}
                          marginBottom={4}
                        />
                        {/*{hasInstantTourAvailability && equalCTAIntent === TOUR && (*/}
                        {/*  <KnockFooter ctaText="Instant book" />*/}
                        {/*)}*/}
                        {!entity.isRentpath && alcCheckbox}
                      </>
                    )}
                  </>
                ) : null}
              </Form>
            )}
          </>
        )}
      </Formik>
    </Box>
  )
}

export const NewMessageForm = <Entity extends Listable | Property, _Origin extends Origin>({
  hasContext = true,
  ...props
}: NewMessageFormProps<Entity, _Origin>) => {
  if (hasContext)
    return (
      <MessageFormContextProvider<Values>>
        <MessageForm {...props} />
      </MessageFormContextProvider>
    )

  return <MessageForm {...props} />
}

export default NewMessageForm
